import { compressToBase64, decompressFromBase64 } from 'lz-string';
import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useFragment, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { ProductStatus } from 'src/shared/enums';
import { T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { createModelFromDto, toUpdateMutationInput } from '../productConfiguration/state/model/model';
import { fetchProductCompressedQuery } from '../productConfiguration/state/queries/__generated__/fetchProductCompressedQuery.graphql';
import { fetchProductQuery } from '../productConfiguration/state/queries/__generated__/fetchProductQuery.graphql';
import { productFragment_model$data, productFragment_model$key } from '../productConfiguration/state/queries/__generated__/productFragment_model.graphql';
import { productFragment_modelCompressed$key } from '../productConfiguration/state/queries/__generated__/productFragment_modelCompressed.graphql';
import { fetchProduct, fetchProductCompressed } from '../productConfiguration/state/queries/fetchProduct';
import { productFragment, productFragmentCompressed } from '../productConfiguration/state/queries/productFragment';
import { getAllSidesFromQueryModel } from '../productConfiguration/state/side';
import previewUpdateMutationNode, { previewUpdateMutation } from '../productConfiguration/steps/5-preview/__generated__/previewUpdateMutation.graphql';

// The direct copy reuses the customizer save: configurator products keep their data in the scene and
// composer, which that save does not carry, so they are copied from the editor only.
export const canCopyDemoProduct = (zakekeEnvironment: string | null | undefined) => zakekeEnvironment === 'CUSTOMIZER';

interface CopyCallbacks {
    onCopied: (modelID: number | null) => void;
    onFailed: () => void;
}

// Saves the demo product as a new product, the same way the editor does on «Duplicate and save».
const DemoProductCopyRunner: FC<CopyCallbacks & { product: productFragment_model$data | null }> = ({ product, onCopied, onFailed }) => {
    const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const [commitUpdate] = useMutation<previewUpdateMutation>(previewUpdateMutationNode);
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        if (!product?.isDemo) {
            onFailed();
            return;
        }

        const sides = getAllSidesFromQueryModel(product);
        const input = toUpdateMutationInput(ProductStatus.UnPublished, product.salesChannelID ?? '', createModelFromDto(product, sides), sides);

        commitUpdate({
            variables: config.enableCompressionForSavingReadingProducts ? { compressedInput: compressToBase64(JSON.stringify(input)) } : { input },
            updater: (store) => store.invalidateStore(),
            onCompleted: (response) => {
                invalidate();
                onCopied(response.createOrUpdateModelV2.model?.modelID ?? null);
            },
            onError: onFailed
        });
    }, []);

    return null;
};

const DemoProductCopyLoader: FC<CopyCallbacks & { productID: string }> = ({ productID, ...callbacks }) => {
    const query = useLazyLoadQuery<fetchProductQuery>(fetchProduct, { id: productID }, { fetchPolicy: 'network-only' });
    const product = useFragment<productFragment_model$key>(productFragment, query.node?.productFragment_model ?? null) ?? null;

    return <DemoProductCopyRunner product={product} {...callbacks} />;
};

const DemoProductCopyLoaderCompressed: FC<CopyCallbacks & { productID: string }> = ({ productID, ...callbacks }) => {
    const query = useLazyLoadQuery<fetchProductCompressedQuery>(fetchProductCompressed, { id: productID }, { fetchPolicy: 'network-only' });
    const compressed = useFragment<productFragment_modelCompressed$key>(productFragmentCompressed, query.node?.productFragment_modelCompressed ?? null);
    const product = compressed
        ? ({
              ...compressed,
              colors: compressed.modelColorsCompressed ? JSON.parse(decompressFromBase64(compressed.modelColorsCompressed)) : []
          } as unknown as productFragment_model$data)
        : null;

    return <DemoProductCopyRunner product={product} {...callbacks} />;
};

/**
 * Copies a demo product without opening the editor. Render `copyDemoProductElement` somewhere in the
 * component tree: it loads the product only while a copy is running.
 */
export const useCopyDemoProduct = (onCopied?: (modelID: number | null) => void) => {
    const { isAllowed } = usePermissions();
    const [copyingProductID, setCopyingProductID] = useState<string | null>(null);
    const loadingNotification = useRef<string | null>(null);

    const copyDemoProduct = useCallback(
        (productID: string) => {
            if (copyingProductID) return;

            if (!isAllowed(['PRODUCTS_EDIT'])) {
                showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
                return;
            }

            loadingNotification.current = showNotification({ type: 'default', message: T._('Duplicating the demo product...', 'Admin') });
            setCopyingProductID(productID);
        },
        [copyingProductID, isAllowed]
    );

    const finish = () => {
        if (loadingNotification.current) removeNotification(loadingNotification.current);
        loadingNotification.current = null;
        setCopyingProductID(null);
    };

    const handleCopied = (modelID: number | null) => {
        finish();
        showSuccessNotification({
            message: T._(
                'Demo product duplicated. You now have a regular product you can use standalone or connect to a store product to make it customizable for your customers.',
                'Admin'
            ),
            dismiss: { duration: 8000 }
        });
        onCopied?.(modelID);
    };

    const handleFailed = () => {
        finish();
        showErrorNotification({ message: T._('An error occurred while duplicating the demo product. Please try again.', 'Admin') });
    };

    const Loader = config.enableCompressionForSavingReadingProducts ? DemoProductCopyLoaderCompressed : DemoProductCopyLoader;

    const copyDemoProductElement = copyingProductID ? (
        <Suspense fallback={null}>
            <Loader key={copyingProductID} productID={copyingProductID} onCopied={handleCopied} onFailed={handleFailed} />
        </Suspense>
    ) : null;

    return { copyDemoProduct, isCopyingDemoProduct: copyingProductID != null, copyDemoProductElement };
};
